import { useCallback, useEffect, useState } from "react";

import { api, clinicHeader, readError } from "../api/client";

/**
 * Mark this patient as here.
 *
 * On the chart rather than on a separate screen, because that is where the
 * front desk already is when somebody hands over their card.
 *
 * It offers nothing to a patient who is already waiting or already with a
 * clinician, and the server refuses either way (SCH-F-040). Nothing here gates
 * care: a doctor opens a visit whether this was ever pressed or not.
 */
export function CheckInButton({ patientId, onChanged }: { patientId: string; onChanged: () => void }) {
  const [here, setHere] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const look = useCallback(async () => {
    const { data } = await api.GET("/portal/queue", { params: { header: clinicHeader() } });
    const mine = (data?.items ?? []).find((r) => r.patient_id === patientId);
    setHere(mine?.status ?? null);
  }, [patientId]);

  useEffect(() => {
    void look();
  }, [look]);

  const checkIn = async () => {
    setBusy(true);
    const { error: failed } = await api.POST("/portal/patients/{id}/check-in", {
      params: { path: { id: patientId }, header: clinicHeader() },
      body: {},
    });
    setBusy(false);
    if (failed) {
      setError(readError(failed).message);
      return;
    }
    setError(null);
    await look();
    onChanged();
  };

  if (here === "arrived") return <span className="queued-state arrived">Waiting</span>;
  if (here === "in_progress") return <span className="queued-state in_progress">In consultation</span>;

  return (
    <>
      <button type="button" disabled={busy} onClick={checkIn}>
        {busy ? "Checking in\u2026" : "Check in"}
      </button>
      {error && <span className="error">{error}</span>}
    </>
  );
}
